import React, { useEffect, useState } from 'react';
// PUBLIC SITE: No MSAL imports - this is an unauthenticated public site
import { useSearchParams } from 'react-router-dom';
import { useUserType, USER_TYPES } from 'utils/userTypeContext';
import { useTranslation } from 'utils/i18n';
import { Icons, ResaleCheckoutModal, RegionProviderModal } from 'coreIndex';
import { isAuthPortal, parseDomain, mapRegionCodeToName } from 'utils/domainParser';
import CustomerHomepage from './CustomerHomepage';

// Import extracted logic
import { useResaleFlow } from './logic/ui';
import {
  AUDIENCE,
  migrateAudiencePreferenceStorage,
} from 'utils/audienceParams';

/**
 * Home Page Component for platform-public (unauthenticated site)
 * Always shows the bot-focused customer homepage (single audience).
 *
 * Note: Authenticated dashboards are in platform-auth.
 * Also handles resale checkout flow for customer signup.
 */
const HomePage = () => {
  if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
    console.log('[PP-DBG] 14 HomePage render');
  }
  const { setUserType } = useUserType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const currentPort = typeof window !== 'undefined' ? window.location.port : '';
  const isPublicPort = currentPort === '4001' || currentPort === '4003' || currentPort === '5003' || parseInt(currentPort, 10) === 4001 || parseInt(currentPort, 10) === 4003 || parseInt(currentPort, 10) === 5003;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isOnAuthPortal = isAuthPortal(hostname);
  const isAuthenticated = false;
  const showPublicView = searchParams.get('view') === 'public';

  const [showRegionProviderModal, setShowRegionProviderModal] = useState(false);

  useEffect(() => {
    migrateAudiencePreferenceStorage();
    setUserType(USER_TYPES.CUSTOMER);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('audiencePreference', AUDIENCE.BOT);
    }
  }, [setUserType]);

  useEffect(() => {
    if (searchParams.get('audience') === AUDIENCE.BOT) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('audience', AUDIENCE.BOT);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    const currentHostname = window.location.hostname;
    const currentPort = window.location.port;
    const currentIsOnAuthPortal = isAuthPortal(currentHostname);
    const selectedRegion = localStorage.getItem('selectedRegion');
    const selectedProvider = localStorage.getItem('selectedProvider');

    const portNum = parseInt(currentPort, 10);
    if (
      currentPort === '4001' ||
      currentPort === '4003' ||
      currentPort === '5003' ||
      portNum === 4001 ||
      portNum === 4003 ||
      portNum === 5003
    ) {
      setShowRegionProviderModal(false);
      return;
    }

    if (currentIsOnAuthPortal && !isAuthenticated) {
      if (!selectedRegion || !selectedProvider) {
        setShowRegionProviderModal(true);
        return;
      }
    }

    if (!currentIsOnAuthPortal || (isAuthenticated || (selectedRegion && selectedProvider))) {
      setShowRegionProviderModal(false);
    }
  }, [isOnAuthPortal, isAuthenticated, searchParams]);

  const handleRegionProviderSelect = (selection) => {
    setShowRegionProviderModal(false);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedProvider', selection.provider);
      localStorage.setItem('selectedRegion', selection.region);
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('region', selection.region);
    newParams.set('provider', selection.provider);
    setSearchParams(newParams);
  };

  const domainInfo = hostname ? parseDomain(hostname) : null;
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();

  let initialProvider = urlParams.get('provider') || (typeof window !== 'undefined' ? localStorage.getItem('selectedProvider') : null) || 'az';
  let initialRegion = urlParams.get('region') || (typeof window !== 'undefined' ? localStorage.getItem('selectedRegion') : null) || 'germanywestcentral';

  if (domainInfo) {
    const regionName = mapRegionCodeToName(domainInfo.region);
    if (regionName) {
      initialRegion = regionName;
    }
    if (domainInfo.provider) {
      initialProvider = domainInfo.provider;
    }
  }

  const { showResaleCheckout, resaleContext, handleClose } = useResaleFlow(
    undefined,
    searchParams,
    setSearchParams
  );

  const homepage = <CustomerHomepage />;

  if (showPublicView) {
    return (
      <div className="main-content">
        {homepage}

        <button
          className="back-to-dashboard-btn"
          onClick={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('view');
            setSearchParams(newParams);
          }}
          title={t('header.backToDashboard', 'Back to dashboard')}
          aria-label={t('header.backToDashboard', 'Back to dashboard')}
        >
          <Icons.ArrowLeft size={16} />
          <span>{t('header.backToDashboard', 'Back to Dashboard')}</span>
        </button>

        {showResaleCheckout && (
          <ResaleCheckoutModal
            isOpen={showResaleCheckout}
            onClose={handleClose}
            resaleContext={resaleContext}
          />
        )}
      </div>
    );
  }

  return (
    <div className="main-content">
      {showRegionProviderModal && (
        <RegionProviderModal
          onSelect={handleRegionProviderSelect}
          initialProvider={initialProvider}
          initialRegion={initialRegion}
        />
      )}
      {!showRegionProviderModal && homepage}

      {showResaleCheckout && (
        <ResaleCheckoutModal
          isOpen={showResaleCheckout}
          onClose={handleClose}
          resaleContext={resaleContext}
        />
      )}
    </div>
  );
};

export default HomePage;
